<template>
  <div>
    <p class="customers-locations-subtitle">
      {{ $t('customersLocationsManagement.subtitle') }}
    </p>

    <CustomersLocationsSearch
      @handle-customers-locations-list-update="handleCustomerSelection"
    />

    <template v-if="customer">
      <CustomerSynopsis :customer="customer" />

      <TableWrapper
        :table-columns="customersLocationsColumns"
        table-key="customersLocations"
        @editCustomerLocation="openEditModal"
        @deleteCustomerLocation="deleteCustomerLocation"
      />

      <ViewActionButtons :buttons="actionButtons" />

      <CreateEditCustomerLocation
        v-if="openCreateEditCustomerLocation"
        :is-modal-edit="isModalEdit"
        :initial-data="initialCustomerLocationData"
        :customer="customer"
        @dialogClose="handleDialogClose"
      />
    </template>

    <p
      v-else
      class="customers-locations-empty"
    >
      {{ $t('customersLocationsManagement.noCustomer') }}
    </p>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import { customersLocationsColumns } from '@/config';
  import { customersApi, locationsManagementApi } from '@/api';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';

  import CustomersLocationsSearch from './CustomersLocationsSearch.vue';
  import CustomerSynopsis from './CustomerSynopsis.vue';
  import CreateEditCustomerLocation from './CreateEditCustomerLocation.vue';

  export default {
    name: 'CustomersLocationsManagementIndex',
    components: {
      TableWrapper,
      ViewActionButtons,
      CustomersLocationsSearch,
      CustomerSynopsis,
      CreateEditCustomerLocation,
    },
    data() {
      return {
        customersLocationsColumns,
        customer: null,
        openCreateEditCustomerLocation: false,
        isModalEdit: false,
        initialCustomerLocationData: {},
        actionButtons: [
          {
            text: this.$t(
              'customersLocationsManagement.create.createAssociation'
            ),
            type: 'primary',
            permissions: ['locationCustomerDepartmentManage'],
            action: () => {
              this.openCreateEditCustomerLocation = true;
            },
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'forms']),
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'resetTable',
        'parseQueryStringStatus',
      ]),
      notifyError(error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message:
            error.response?.data?.error?.message ?? this.$t('global.error'),
        });
      },
      handleCustomerSelection() {
        const customerId = this.forms.customersLocations.id || undefined;
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              customerId,
              tableKey: 'customersLocations',
              page: 1,
            },
          })
          .catch(() => {});
      },
      async loadCustomer(customerId) {
        if (!customerId) {
          this.customer = null;
          this.resetTable('customersLocations');
          return;
        }
        if (String(this.customer?.id) !== String(customerId)) {
          try {
            const response = await customersApi.getCustomer(customerId);
            this.customer = response.data;
          } catch (error) {
            this.customer = null;
            this.resetTable('customersLocations');
            this.notifyError(error);
            return;
          }
        }
        await this.getCustomersLocations(customerId);
      },
      async getCustomersLocations(customerId) {
        try {
          const customerLocationsIndex =
            await locationsManagementApi.getCustomerLocations(
              this.tableFilters('customersLocations'),
              customerId
            );
          const customerLocationsCount =
            await locationsManagementApi.customerLocationsCount(customerId);
          this.updateTableRows({
            tableKey: 'customersLocations',
            rows: customerLocationsIndex.data,
          });
          this.updateTableCount({
            tableKey: 'customersLocations',
            count: customerLocationsCount.data.count,
          });
        } catch (error) {
          this.notifyError(error);
        }
      },
      handleDialogClose(status) {
        this.openCreateEditCustomerLocation = false;
        if (this.isModalEdit) this.isModalEdit = false;
        if (status.success) this.getCustomersLocations(this.customer.id);
      },
      openEditModal(row) {
        this.initialCustomerLocationData = row;
        this.isModalEdit = true;
        this.openCreateEditCustomerLocation = true;
      },
      deleteCustomerLocation(row) {
        this.$confirm(
          this.$t('global.confirmAction'),
          this.$t('global.warning'),
          {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.cancel'),
          }
        ).then(async () => {
          try {
            await locationsManagementApi.customerLocationsDelete(row.id);
          } catch (error) {
            this.notifyError(error);
            return;
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('customersLocationsManagement.create.success'),
          });
          this.getCustomersLocations(this.customer.id);
        });
      },
    },
    created() {
      this.parseQueryStringStatus(this.$route.query);
      this.loadCustomer(this.$route.query.customerId);
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.loadCustomer(to.query.customerId);
      next();
    },
  };
</script>

<style lang="scss">
  .customers-locations-subtitle {
    color: $--color-white;
    padding-bottom: 1rem;
  }

  .customers-locations-empty {
    color: $--color-text-secondary;
    padding-top: 1rem;
  }
</style>
